import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, Linkedin, Loader2, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApplicationModal = ({ isOpen, onClose, jobId, jobTitle, companyName }) => {
  const { toast } = useToast();
  
  const [step, setStep] = useState(1); // 1: method, 2: form, 3: success
  const [loading, setLoading] = useState(false);
  const [applicationMethod, setApplicationMethod] = useState('platform');
  
  // Form data
  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    applicant_linkedin: '',
    cover_letter: ''
  });
  
  const [resume, setResume] = useState(null);
  const [matchScore, setMatchScore] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Resume must be less than 5MB',
          variant: 'destructive'
        });
        return;
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload PDF, DOC, DOCX, or TXT',
          variant: 'destructive'
        });
        return;
      }
      
      setResume(file);
    }
  };

  const handleLinkedInApply = () => {
    // OAuth flow would go here
    toast({
      title: 'Coming Soon',
      description: 'LinkedIn OAuth integration will be available soon',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.applicant_name || !formData.applicant_email) {
      toast({
        title: 'Required Fields',
        description: 'Please fill in name and email',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('applicant_name', formData.applicant_name);
      formDataToSend.append('applicant_email', formData.applicant_email);
      formDataToSend.append('applicant_phone', formData.applicant_phone || '');
      formDataToSend.append('applicant_linkedin', formData.applicant_linkedin || '');
      formDataToSend.append('cover_letter', formData.cover_letter || '');
      formDataToSend.append('applied_via', applicationMethod);
      
      if (resume) {
        formDataToSend.append('resume', resume);
      }

      const response = await fetch(`${BACKEND_URL}/api/jobs/${jobId}/apply`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        const data = await response.json();
        setMatchScore(data.match_score);
        setStep(3);
        
        toast({
          title: 'Application Submitted!',
          description: `Your match score is ${data.match_score}%`,
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to submit application',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit application',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      applicant_name: '',
      applicant_email: '',
      applicant_phone: '',
      applicant_linkedin: '',
      cover_letter: ''
    });
    setResume(null);
    setMatchScore(null);
    setApplicationMethod('platform');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Apply for {jobTitle}
          </DialogTitle>
          <p className="text-sm text-slate-600">{companyName}</p>
        </DialogHeader>

        {/* Step 1: Choose Application Method */}
        {step === 1 && (
          <div className="space-y-6 mt-4">
            <div>
              <h3 className="font-semibold text-lg mb-4">How would you like to apply?</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Platform Apply */}
                <button
                  onClick={() => {
                    setApplicationMethod('platform');
                    setStep(2);
                  }}
                  className="p-6 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Apply with Resume</h4>
                      <p className="text-xs text-slate-600">Standard application</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Upload your resume and fill out the application form
                  </p>
                </button>

                {/* LinkedIn Apply */}
                <button
                  onClick={handleLinkedInApply}
                  className="p-6 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Linkedin className="w-6 h-6 text-blue-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Apply with LinkedIn</h4>
                      <p className="text-xs text-slate-600">Quick apply</p>
                      <Badge variant="outline" className="mt-1 text-xs">Coming Soon</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Use your LinkedIn profile to apply instantly
                  </p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Application Form */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Progress Indicator */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 h-2 bg-blue-600 rounded"></div>
              <div className="flex-1 h-2 bg-slate-200 rounded"></div>
            </div>

            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Personal Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.applicant_name}
                  onChange={(e) => setFormData({...formData, applicant_name: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.applicant_email}
                  onChange={(e) => setFormData({...formData, applicant_email: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.applicant_phone}
                  onChange={(e) => setFormData({...formData, applicant_phone: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  value={formData.applicant_linkedin}
                  onChange={(e) => setFormData({...formData, applicant_linkedin: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://linkedin.com/in/johndoe"
                />
              </div>
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Resume / CV
              </label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.txt"
                  className="hidden"
                  id="resume-upload"
                />
                <label htmlFor="resume-upload" className="cursor-pointer">
                  {resume ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">{resume.name}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setResume(null);
                        }}
                        className="text-red-600 text-xs ml-2"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-slate-500">
                        PDF, DOC, DOCX, or TXT (max 5MB)
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Cover Letter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Cover Letter
              </label>
              <textarea
                value={formData.cover_letter}
                onChange={(e) => setFormData({...formData, cover_letter: e.target.value})}
                rows={4}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Tell us why you're a great fit for this role..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                disabled={loading}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            </div>
          </form>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Application Submitted!
            </h3>
            
            <p className="text-slate-600 mb-6">
              Your application for <span className="font-semibold">{jobTitle}</span> has been successfully submitted.
            </p>

            {matchScore !== null && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <p className="text-sm text-slate-600 mb-2">Your Match Score</p>
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {matchScore}%
                </div>
                <p className="text-sm text-slate-600">
                  {matchScore >= 80 ? 'Excellent match!' : 
                   matchScore >= 60 ? 'Good match!' : 
                   'We\'ll review your application'}
                </p>
              </div>
            )}

            <p className="text-sm text-slate-600 mb-6">
              The hiring team will review your application and contact you if there's a match.
            </p>

            <Button
              onClick={handleClose}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationModal;
